import {Auth0ConfigFragFragment} from "../graphql/queries"
import * as React from "react"
import {Icon, Loader, Menu, Popup, Sidebar} from "semantic-ui-react"
import * as SemanticUIReact from "semantic-ui-react"
import {Auth0LockLink} from "./Auth0LockLink"
import {persistedState} from "../localStorage"
import {Link} from "react-router-dom"
import {isReady, loading, Loading, ready} from "../tsutil"
import {Location} from "history"
import {none} from "fp-ts/lib/Option"
import {Booker} from "../model/BookingTypes"
import Responsive from "react-responsive"

interface SidebarProps {
  location: Location
  onClick: () => void
  sidebarOpen: boolean
  booker: Booker | null
}

interface Auth0ConfigProp {
  auth0Config: Loading<Auth0ConfigFragFragment>
}

const unverifiedEmailMessage =
  "Until you verify your account, using the link we sent when " +
  "you signed up, we will not be able to send you any booking emails."

function LoadingMenuItem() {
  return (
    <Menu.Item>
      <Loader inline active size="small" />
    </Menu.Item>
  )
}

export const MenuAsNav = (props: SemanticUIReact.MenuProps) => <Menu as="nav" {...props} />

export function CusoonSidebar(props: SidebarProps) {
  const {booker, onClick, sidebarOpen, location} = props

  return (
    <Sidebar
      as={MenuAsNav}
      animation="overlay"
      icon="labeled"
      vertical
      visible={sidebarOpen}
      width="thin"
    >
      <CusoonMenu onClick={onClick} booker={booker} location={location} />
    </Sidebar>
  )
}

interface MenuProps {
  location: Location
  onClick: () => void
  booker: Booker | null
}

class CusoonMenu extends React.Component<MenuProps, {}> {
  constructor(props: MenuProps) {
    super(props)
  }

  active = (pathname: string) => pathname === this.props.location!.pathname

  handleAddBooking = () => {
    // For an explicit "Make Booking" click we insist on a clean booking slate.
    persistedState.value = none
    this.props.onClick()
  }

  render() {
    const {booker, onClick} = this.props
    const isAdmin = booker && booker.isAdmin

    return (
      <>
        {isAdmin && (
          <Menu.Item as={Link} to={"/admin"} active={this.active("/admin")} onClick={onClick}>
            Admin
          </Menu.Item>
        )}
        <Menu.Item as={"a"} href={"https://cusoon.com.au/"} onClick={onClick} active={false}>
          Home
        </Menu.Item>
        <Menu.Item as={Link} to={"/pricing"} active={this.active("/pricing")} onClick={onClick}>
          Pricing
        </Menu.Item>
        <Menu.Item as={Link} to={"/bookings"} active={this.active("/bookings")} onClick={onClick}>
          Your Bookings
        </Menu.Item>
        <Menu.Item
          as={Link}
          to={"/bookings/add"}
          active={this.active("/bookings/add")}
          onClick={this.handleAddBooking}
        >
          Make Booking
        </Menu.Item>
      </>
    )
  }
}

interface TopMenuProps {
  location: Location
  onClick: () => void
  booker: Booker | null
  auth0Config: Loading<Auth0ConfigFragFragment>
  onOpenOrClose: () => void
}

export function CusoonTopMenu(props: TopMenuProps) {
  const {booker, onClick, location, auth0Config, onOpenOrClose} = props
  return (
    <MenuAsNav>
      <Responsive maxWidth={767}>
        <Menu.Item>
          <Icon name="bars" onClick={onOpenOrClose} />
        </Menu.Item>
      </Responsive>
      <Responsive minWidth={768}>
        <CusoonMenu onClick={onClick} booker={booker} location={location} />
      </Responsive>
      <RightMenu booker={booker} auth0Config={auth0Config} onClick={onClick} />
    </MenuAsNav>
  )
}

function RightMenu(props: {
  booker: Booker | null
  auth0Config: Loading<Auth0ConfigFragFragment>
  onClick: () => void
}) {
  const {booker, auth0Config, onClick} = props
  return booker ? (
    <Menu.Menu position="right">
      <UnverifiedEmailNotice isEmailVerified={booker ? booker.isEmailVerified : true} />
      <EmailMenuItem booker={booker} onClick={onClick} />
      <LogoutMenuItem auth0Config={auth0Config} />
    </Menu.Menu>
  ) : (
    <Menu.Menu position="right">
      <Menu.Item as={Auth0LockLink} initialScreen="forgotPassword">
        Forgot Password
      </Menu.Item>
      <Menu.Item as={Auth0LockLink} initialScreen="signUp">
        Sign Up
      </Menu.Item>
      <Menu.Item as={Auth0LockLink} initialScreen="login">
        Log In
      </Menu.Item>
    </Menu.Menu>
  )
}

function LogoutMenuItem(props: Auth0ConfigProp) {
  return handleLogout(props)
    .map((handleLogout) => <Menu.Item onClick={handleLogout}>Logout</Menu.Item>)
    .getOrElseL(() => <LoadingMenuItem />)
}

function handleLogout(props: Auth0ConfigProp): Loading<() => void> {
  const {auth0Config} = props

  if (!isReady(auth0Config)) {
    return loading
  }

  return ready(() => {
    persistedState.value = none
    // See http://stackoverflow.com/a/506004/3509004
    window.location.href = auth0Config.value.logoutUrl
  })
}

function EmailMenuItem(props: {booker: Booker | null; onClick: () => void}) {
  const {booker, onClick} = props
  return booker ? (
    <Popup
      trigger={
        <Menu.Item as={Link} to={"/profile/" + booker.id} onClick={onClick}>
          {booker.email}
        </Menu.Item>
      }
      content="View and edit your profile, billing address etc."
    />
  ) : null
}

function UnverifiedEmailNotice(props: {isEmailVerified: boolean}) {
  const {isEmailVerified} = props
  return isEmailVerified === false ? (
    <Popup
      trigger={
        <Menu.Item>
          <Icon name="alarm" color="orange" />
        </Menu.Item>
      }
      content={unverifiedEmailMessage}
    />
  ) : null
}



// WEBPACK FOOTER //
// ./src/components/CusoonNav.tsx